<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import route from "ziggy-js"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/toast/use-toast"

type Employee = {
    id: number
    name: string
    employee_number: string
    title: string
    is_active: boolean
}

type Attendance = {
    id: number
    employee_id: number
    work_date: string
    time_in: string | null
    time_out: string | null
}

type StoreHours = {
    opening_login_at?: string
    default_start: string
    default_end: string
    opens_label: string
    closes_label: string
}

const props = defineProps<{
    open: boolean
    date: string
    employees: Employee[]
    storeHours: StoreHours
    attendance?: Attendance | null
}>()

const emit = defineEmits<{
    "update:open": [value: boolean]
}>()

const { toast } = useToast()
const isEditing = computed(() => Boolean(props.attendance))

const form = useForm({
    employee_id: "",
    work_date: props.date,
    time_in: "",
    time_out: "",
})

watch(
    () => [props.open, props.attendance, props.date] as const,
    () => {
        if (!props.open) {
            return
        }

        form.clearErrors()

        if (props.attendance) {
            form.employee_id = String(props.attendance.employee_id)
            form.work_date = props.attendance.work_date
            form.time_in = props.attendance.time_in ?? ""
            form.time_out = props.attendance.time_out ?? ""
            return
        }

        form.reset()
        form.employee_id = ""
        form.work_date = props.date
        form.time_in = props.storeHours.opening_login_at || props.storeHours.default_start
        form.time_out = props.storeHours.default_end
    }
)

function close() {
    emit("update:open", false)
}

function submit() {
    const options = {
        preserveScroll: true,
        onSuccess() {
            close()
            toast({
                description: isEditing.value ? "Attendance updated." : "Attendance added.",
            })
        },
    }

    if (isEditing.value && props.attendance) {
        form.put(route("attendances.update", props.attendance.id), options)
        return
    }

    form.post(route("attendances.store"), options)
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="max-w-md">
            <form class="space-y-4" @submit.prevent="submit">
                <DialogHeader>
                    <DialogTitle>{{ isEditing ? "Edit attendance" : "Add attendance" }}</DialogTitle>
                    <DialogDescription>
                        Store hours are {{ storeHours.opens_label }} to {{ storeHours.closes_label }}. Overnight time
                        out is counted on the next morning.
                    </DialogDescription>
                </DialogHeader>

                <div class="space-y-2">
                    <Label>Staff</Label>
                    <Select v-model:model-value="form.employee_id">
                        <SelectTrigger>
                            <SelectValue placeholder="Select staff" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem v-for="employee in employees" :key="employee.id" :value="String(employee.id)">
                                {{ employee.employee_number }} · {{ employee.name }}
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <p v-if="form.errors.employee_id" class="text-sm text-destructive">{{ form.errors.employee_id }}</p>
                </div>

                <div class="space-y-2">
                    <Label>Date</Label>
                    <Input v-model="form.work_date" type="date" />
                    <p v-if="form.errors.work_date" class="text-sm text-destructive">{{ form.errors.work_date }}</p>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="space-y-2">
                        <Label>Time in</Label>
                        <Input v-model="form.time_in" type="time" required />
                        <p v-if="form.errors.time_in" class="text-sm text-destructive">{{ form.errors.time_in }}</p>
                    </div>
                    <div class="space-y-2">
                        <Label>Time out</Label>
                        <Input v-model="form.time_out" type="time" />
                        <p v-if="form.errors.time_out" class="text-sm text-destructive">{{ form.errors.time_out }}</p>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" @click="close">Cancel</Button>
                    <Button type="submit" :disabled="form.processing">
                        {{ isEditing ? "Save changes" : "Add attendance" }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
